import { updateEndUserIdentity } from 'api/events'
import store, { getStoreState } from 'config/store'
import { receiveEndUser } from 'modules/session/actions'
import { getIntegrationCookiesFromStore } from 'modules/session/selectors'
import { getExternalEmail, isSignedIdentityRequired, isWaitingForUserJwt,resolveExternalIdAndUserJwt } from 'modules/session/utils'
import { isNil, reject } from 'ramda'
import { DRIFT_ANONYMOUS_ID_COOKIE, DRIFT_ANONYMOUS_ID_COOKIE_V1 } from 'shared/constants/cookies'
import { UserIdentity } from 'types/auth'
import { CustomAttributes } from 'types/user'
import { interval,isNilOrEmpty,log, uuid } from 'utils'
import { getCookieValue, setPersistedCookie } from 'utils/cookies'
import { validateEmail } from 'utils/email'

import { receiveIdentityMap } from './actions'
import DemandbaseIdentityProvider from './providers/demandbase'
import EloquaIdentityProvider from './providers/eloqua'
import HubspotIdentityProvider from './providers/hubspot'
import MarketoIdentityProvider from './providers/marketo'
import OneshotTokenIdentityProvider from './providers/oneshotToken'
import PardotIdentityProvider from './providers/pardot'

const MD5_HASH_EMPTY_STRING = 'd41d8cd98f00b204e9800998ecf8427e'

export const getIntegrationIdentityContext = () => {
  const state = getStoreState()

  const integrationCookies = getIntegrationCookiesFromStore(state)

  const { identity = {} } = state
  const { hubspot_cookie } = (identity as UserIdentity)
  let integrationContext = {
    ...MarketoIdentityProvider.getIntegrationContext(integrationCookies),
    ...EloquaIdentityProvider.getIntegrationContext(integrationCookies),
    ...PardotIdentityProvider.getIntegrationContext(integrationCookies),
    ...DemandbaseIdentityProvider.getIntegrationContext(integrationCookies)
  }

  if (integrationCookies?.demandbase){
    integrationContext['demandbase'] = integrationCookies.demandbase
  }

  if (hubspot_cookie) {
    integrationContext['hubspot'] = {
      utk: hubspot_cookie.utk,
      isNewUtk: hubspot_cookie.isNewUtk
    }
  }

  return integrationContext
}

export const generateIdentity = async (): Promise<UserIdentity> => {
  const email = getExternalEmail()

  const anonymousId = await generateAnonymousIdentity()
  store.dispatch(receiveEndUser({ endUser: { leadId: anonymousId }}))

  const { externalId, userJwt } = await resolveExternalIdAndUserJwt()
  const hasExternalIdentifier = !!externalId || !!userJwt

  const identifierMap: UserIdentity = hasExternalIdentifier ?
    await generateExternalIdMap({ id: externalId, email, userJwt, anonymousId }) :
    await resolveIdentityProviders(email, anonymousId)

  const userIdentity: UserIdentity = reject(isNil, identifierMap)

  store.dispatch(receiveIdentityMap(userIdentity))
  return userIdentity
}

export const updateIdentity = async (attributes: CustomAttributes, userId?: string | number, options?: Record<string, any>) => {
  try {
    if (!!userId && !isValidExternalId(userId)) {
      log({ data: ['Unable to identify due to undefined userId'] })
      return
    }

    let identityOptions = { ...options }
    if (userId && !identityOptions.userJwt) {
      const { userJwt } = await resolveExternalIdAndUserJwt()
      if (userJwt) {
        identityOptions.userJwt = userJwt
      }
    }

    await updateEndUserIdentity(attributes, userId, identityOptions)
    // eslint-disable-next-line no-console
    console.debug("Drift: Identity Successfully Updated")
  } catch (err) {
    log({ data: [err], type: 'warn' })
  }
}

const resolveIdentityProviders = async (email: string, leadId: string): Promise<UserIdentity> => {
  let identifierMap: UserIdentity = {}

  identifierMap.lead_id = leadId
  identifierMap = Object.assign(identifierMap, await resolveDirectIdentityProviders(identifierMap, email))
  identifierMap = Object.assign(identifierMap, await resolveThirdPartyIdentityProviders(identifierMap))

  return identifierMap
}

const resolveDirectIdentityProviders = async (identityMap: UserIdentity, email: string): Promise<UserIdentity> => {
  if (validateEmail(email)) {
    identityMap['email'] = email
  }

  resolveOneShotIdentity(identityMap)
  return identityMap
}

const resolveOneShotIdentity = (identityMap: UserIdentity) => {
  const oneshotIdentity = OneshotTokenIdentityProvider.identify()
  if (!!oneshotIdentity) {
    identityMap['oneshot_token'] = oneshotIdentity
  }
}

const resolveThirdPartyIdentityProviders = async (identityMap: UserIdentity): Promise<UserIdentity> => {
  const state = getStoreState()
  const { gates = {} } = state.embed.configuration
  const integrationCookies = await getIntegrationCookiesFromStore(state)

  const anonymousId = identityMap.lead_id

  // collect all data for providers
  const hubspotIdentity = await HubspotIdentityProvider.identify(anonymousId)
  const marketoCookie = MarketoIdentityProvider.identify(integrationCookies)
  const pardotData = PardotIdentityProvider.identify(integrationCookies)
  const eloquaData = EloquaIdentityProvider.identify(integrationCookies)
  const allPardotData = integrationCookies?.PARDOT_ALL_COOKIES

  // legacy ID data for bootstrap
  if (HubspotIdentityProvider.isEnabled(gates)) {
    if (!!hubspotIdentity) {
      identityMap['hubspot_cookie'] = hubspotIdentity

      if (hubspotIdentity.email) {
        identityMap['email'] = hubspotIdentity?.email
      }
    }
  }

  // This represents whether or not the Marketo integration has the toggle enabled to identify site visitors via Marketo API
  identityMap['marketo_identity'] = MarketoIdentityProvider.getIdentityEnabled()
  identityMap['marketo_cookie'] = marketoCookie
  identityMap['pardot_cookie'] = PardotIdentityProvider.isEnabled(gates) ? pardotData : null
  identityMap['eloqua_cookie'] = EloquaIdentityProvider.isEnabled(gates) ? eloquaData : null

  const pardotAggregated = {...allPardotData}

  // new contract when embed is part of bootstrap
  identityMap['integrations'] = {
    'hubspot': hubspotIdentity,
    'email': hubspotIdentity?.email,
    'marketo': marketoCookie,
    'eloqua': eloquaData
  }

  if (!isNilOrEmpty(integrationCookies?.demandbase)){
    identityMap['demandbase'] = integrationCookies.demandbase
  }

  if (!isNilOrEmpty(pardotAggregated)) {
    identityMap['integrations']['pardot'] = pardotAggregated
  }

  return identityMap
}

export const generateAnonymousIdentity = async (): Promise<string> => {
  const existingAnonymousIdCookie = await getCookieValue(DRIFT_ANONYMOUS_ID_COOKIE)
  const anonymousIdCookieFromV1 = await getCookieValue(DRIFT_ANONYMOUS_ID_COOKIE_V1)

  if (!existingAnonymousIdCookie) {

    if (anonymousIdCookieFromV1) {
      setPersistedCookie(DRIFT_ANONYMOUS_ID_COOKIE, anonymousIdCookieFromV1)
      return anonymousIdCookieFromV1
    }

    const newAnonymousId = uuid()
    setPersistedCookie(DRIFT_ANONYMOUS_ID_COOKIE, newAnonymousId)
    setPersistedCookie(DRIFT_ANONYMOUS_ID_COOKIE_V1, newAnonymousId)
    return newAnonymousId
  }

  return existingAnonymousIdCookie
}

type GenerateExternalIdMapArgs = {
  id: string,
  email: string,
  userJwt?: string,
  anonymousId: string
}

const generateExternalIdMap = async ({ id, email, userJwt, anonymousId }: GenerateExternalIdMapArgs): Promise<UserIdentity> => {
  let externalIdMap: UserIdentity = {}
  const isSignedIdentityRequiredWithNoJwt = isSignedIdentityRequired() && !userJwt
  const hasValidExternalId = isValidExternalId(id)

  if (email) {
    externalIdMap.email = email
  }

  // If we have a valid external id, add that to the map - otherwise put the anonymous id in the map as a fallback
  if (hasValidExternalId) {
    externalIdMap.user_id = id
  } else {
    externalIdMap.lead_id = anonymousId
  }

  // If we have a userJwt, we also send up anonymous id as a fallback in case the status is ENABLED and the jwt is not valid
  if (userJwt) {
    externalIdMap.jwt = userJwt
    externalIdMap.lead_id = anonymousId
    delete externalIdMap.user_id
  }

  // If we have no jwt and it's required, auth will fail but we want to send up lead_id to match what the legacy widget does here
  if (isSignedIdentityRequiredWithNoJwt) {
    externalIdMap.lead_id = anonymousId
    delete externalIdMap.user_id
  }

  // If we have a token via oneshot (i.e. d_utk={x}) add that to the authentication call so we can provide the userId
  // in the token inside of the assignedAgentId field in the end user that comes back from bootstrap
  resolveOneShotIdentity(externalIdMap)
  return externalIdMap
}

export const isValidExternalId = (externalId: string | number): boolean => {
  if (!externalId) {
    return false
  }

  const isStringUndefined = externalId === 'undefined'
  const isUndefinedIdentity = externalId === 'undefined-undefined'
  const isUndefinedMd5Identity = externalId === MD5_HASH_EMPTY_STRING

  return !isStringUndefined && !isUndefinedIdentity && !isUndefinedMd5Identity
}

export const waitForUserJwt = function () {
  const stopInterval = interval(() => {
    console.warn('Drift Widget is still waiting for user JWT!')
  }, 30000)

  return new Promise((resolve) => {
    const unsubscribe = store.subscribe(async () => {
      if (await isWaitingForUserJwt()) {
        return
      }
      stopInterval()
      unsubscribe()
      resolve(() => {})
    })
  })
}
